import { useEffect, useState } from 'react';

/**
 * Bouncer sprite rendering.
 *
 * The source assets are 318×240 screencaps that arrive **already shrunk and
 * posterized**. They are finished sprites, not input to a pipeline: no second
 * quantization (stacking one on an already-reduced palette gives you banding
 * and dirty edges), no smoothing, no sharpening, no AI upscaler.
 *
 * The rule that matters most is **integer scaling only.** Fractional scaling
 * with `image-rendering: pixelated` produces unevenly sized pixels — some 2px
 * wide, some 3px — which reads as a rendering bug rather than as pixel art.
 *
 * Both bouncers are on screen at once, Armane pinned left and Versace right,
 * with whoever is not speaking dimmed. That is what makes the bribe work:
 * there is always a real portrait to drag the note onto.
 *
 * Two full sprites need 636px, which a 390px phone does not have. Rather than
 * scale down — which would break the integer rule — each portrait sits in a
 * narrower window and is **centre-cropped**. Cropping resamples nothing, the
 * pixels stay uniform, and both faces stay in frame. (DESIGN.md §5: "pick the
 * largest integer factor that fits and letterbox the remainder".)
 */

export const SPRITE_W = 318;
export const SPRITE_H = 240;

/** Breathing room at the outer edges and between the two portraits. */
const GUTTER = 8;

export interface PairLayout {
  /** Integer scale factor applied to both sprites. Never below 1. */
  scale: number;
  /** Visible width of each portrait. Less than the sprite width ⇒ cropped. */
  windowW: number;
  /** Rendered sprite size before cropping. */
  spriteW: number;
  spriteH: number;
}

export function computePairLayout(viewportW: number, availableH: number): PairLayout {
  const half = Math.max(80, (viewportW - GUTTER * 3) / 2);
  const scale = Math.max(1, Math.floor(Math.min(half / SPRITE_W, availableH / SPRITE_H)));
  const spriteW = SPRITE_W * scale;
  const spriteH = SPRITE_H * scale;
  return { scale, windowW: Math.min(spriteW, half), spriteW, spriteH };
}

export function usePairLayout(reserveHeightPx: number): PairLayout {
  const [layout, setLayout] = useState<PairLayout>(() =>
    computePairLayout(
      typeof window === 'undefined' ? 390 : window.innerWidth,
      typeof window === 'undefined' ? 400 : window.innerHeight - reserveHeightPx,
    ),
  );

  useEffect(() => {
    const recompute = () =>
      setLayout(computePairLayout(window.innerWidth, window.innerHeight - reserveHeightPx));
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', recompute);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', recompute);
    };
  }, [reserveHeightPx]);

  return layout;
}

/**
 * One portrait, centre-cropped into its window.
 *
 * The element carries `data-guard`, which is what the bribe uses as a drop
 * target — so the thing you drag the note onto is the actual face, not an
 * invisible rectangle guessed at over a group shot.
 */
export function Portrait({
  guard,
  src,
  hatSrc,
  hat,
  layout,
  dimmed,
}: {
  guard: 'armane' | 'versace';
  src: string;
  /** The titovka, painted on the same 318×240 canvas as the portrait. */
  hatSrc: string;
  hat: boolean;
  layout: PairLayout;
  dimmed: boolean;
}) {
  const overflow = layout.spriteW - layout.windowW;
  // The hat is authored as a layer on the same canvas as the face, so it needs
  // no offset of its own — it lands where it was drawn. That is also why the
  // separate `titovka_*.png` files are used rather than the baked-in
  // `dizelasi_*_titovka.png` portraits: a swap of the whole sprite can only
  // pop, and the beat the hats come off is one you want to *see*.
  //
  // Drop distance scales with the sprite so it looks the same at every integer
  // factor; the tilt is what makes it read as being clapped on rather than
  // faded in.
  //
  // It is `pointer-events-none` because it is a full-size transparent layer
  // over the whole portrait window at *every* node, hat or no hat, and that
  // window is the bribe's drop target. The bribe happens to survive it —
  // `elementsFromPoint` returns the whole stack and the guard div is the hat's
  // own ancestor — but a decorative aria-hidden layer being a hit target at
  // all is a trap for the next thing that hit-tests here.

  return (
    <div
      data-guard={guard}
      className="relative overflow-hidden transition-[filter,opacity] duration-300"
      style={{
        width: layout.windowW,
        height: layout.spriteH,
        // The non-speaker is dimmed and slightly desaturated, so it is always
        // unambiguous who is talking.
        opacity: dimmed ? 0.5 : 1,
        filter: dimmed ? 'saturate(0.45) brightness(0.6)' : 'none',
      }}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        className="sprite absolute top-0 select-none"
        style={{ width: layout.spriteW, height: layout.spriteH, left: -overflow / 2 }}
      />
      <img
        src={hatSrc}
        alt=""
        aria-hidden
        draggable={false}
        className="sprite pointer-events-none absolute top-0 select-none transition-[transform,opacity] duration-300 ease-out"
        style={{
          width: layout.spriteW,
          height: layout.spriteH,
          left: -overflow / 2,
          opacity: hat ? 1 : 0,
          transform: hat
            ? 'translateY(0) rotate(0deg)'
            : `translateY(${-layout.spriteH * 0.22}px) rotate(-9deg)`,
          transformOrigin: '50% 20%',
        }}
      />
    </div>
  );
}

/** The rejection two-shot. One image, so it replaces the pair rather than sitting in it. */
export function TwoShot({ src, layout }: { src: string; layout: PairLayout }) {
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      className="sprite select-none"
      style={{ width: layout.spriteW, height: layout.spriteH }}
    />
  );
}

/**
 * The crunch goes on top as a *layer*, never as a re-encode: scanlines, a
 * vignette, and a hint of chromatic aberration composited over untouched
 * sprites. Pointer-events off — decoration sitting over tap targets.
 */
export function CrtOverlay() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.22] mix-blend-multiply"
        style={{
          backgroundImage:
            'repeating-linear-gradient(to bottom, rgba(0,0,0,.55) 0px, rgba(0,0,0,.55) 1px, transparent 1px, transparent 3px)',
        }}
      />
      <div
        className="absolute inset-0 opacity-30 mix-blend-screen"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(255,0,0,.10), transparent 12%, transparent 88%, rgba(0,180,255,.10))',
        }}
      />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(0,0,0,.7) 100%)' }}
      />
    </div>
  );
}
